import { useCallback, useEffect, useMemo, useState } from "react";
import blogFetch from "../../axios/config";
import React from "react";
import Loading from "../../components/Loading";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar";
import "./style.css"
import { FormatDate } from "../../utils/DataUtils";
import { useDebouncedValue } from "./DataTable";

interface ListaOrdem {
    tela: string[]
    ordens: string[];
    data: Date[];
    lote: string[];
}

/** Linha já pronta para exibição, calculada uma única vez por resposta da API. */
interface LinhaOrdem {
    id: number;
    tela: string;
    ordem: string;
    lote: string;
    data: string;
    busca: string;
}

const PAGE_SIZE = 100;

const texto = (valor: unknown) => (Array.isArray(valor) ? valor.join("") : String(valor ?? ""));

export function ListaOrdens() {
    const [ordens, setOrdens] = useState<ListaOrdem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isGerando, setIsGerando] = useState(false);
    const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
    const [searchInput, setSearchInput] = useState("");
    const [page, setPage] = useState(0);
    const searchTerm = useDebouncedValue(searchInput);

    const fetchOrdens = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data } = await blogFetch.get<ListaOrdem[]>("SAGA_ListaProducao/getListaOrdens");
            setOrdens(data);
        } catch (error) {
            console.error("Erro ao buscar ordens:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrdens();
    }, [fetchOrdens]);

    const linhas = useMemo<LinhaOrdem[]>(() => ordens.map(ordem => {
        const tela = texto(ordem.tela);
        const numero = texto(ordem.ordens);
        const lote = texto(ordem.lote);

        return {
            id: Number(numero),
            tela,
            ordem: numero,
            lote,
            data: FormatDate(ordem.data),
            busca: `${numero} ${tela} ${lote}`.toLowerCase()
        };
    }), [ordens]);

    const linhasFiltradas = useMemo(() => {
        const termo = searchTerm.trim().toLowerCase();
        if (!termo) return linhas;
        return linhas.filter(linha => linha.busca.includes(termo));
    }, [linhas, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(linhasFiltradas.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages - 1);

    useEffect(() => {
        setPage(0);
    }, [searchTerm, linhas]);

    const linhasVisiveis = useMemo(() => {
        const inicio = currentPage * PAGE_SIZE;
        return linhasFiltradas.slice(inicio, inicio + PAGE_SIZE);
    }, [linhasFiltradas, currentPage]);

    // Referência estável: sem ela, cada clique re-renderiza todas as linhas da página.
    const handleCheckboxChange = useCallback((id: number) => {
        setSelecionados(prev => {
            const proximo = new Set(prev);
            if (proximo.has(id)) {
                proximo.delete(id);
            } else {
                proximo.add(id);
            }
            return proximo;
        });
    }, []);

    const handleImprirOrdem = useCallback(async () => {
        if (selecionados.size === 0) {
            alert("Selecione pelo menos um item para reimprimir");
            return;
        }

        setIsGerando(true);
        try {
            const response = await blogFetch.post(
                "SAGA_ListaProducao/imprimirOrdens",
                Array.from(selecionados),
                { responseType: "blob" }
            );

            if (response.data) {
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Ordem.xlsx";
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                setSelecionados(new Set());
                await fetchOrdens();
            }
        } catch (error) {
            console.error("Erro ao baixar o arquivo:", error);
        } finally {
            setIsGerando(false);
        }
    }, [selecionados, fetchOrdens]);

    const primeiraLinha = linhasFiltradas.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
    const ultimaLinha = Math.min(linhasFiltradas.length, (currentPage + 1) * PAGE_SIZE);

    return (
        <>
            <Navbar />
            <Sidebar />
            <div className="app_body container_ordem">
                {isLoading ? (
                    <Loading />
                ) : (
                    <div className="content_ordem">
                        <div className="header_ordem">
                            <div>
                                <h1 className="title_ordem">Lista de Ordens Impressas</h1>
                                <p className="subtitle_ordem">
                                    {linhasFiltradas.length} ordem(ns)
                                    {linhasFiltradas.length !== linhas.length && ` de ${linhas.length}`}
                                    {linhasFiltradas.length > PAGE_SIZE && ` · exibindo ${primeiraLinha}-${ultimaLinha}`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleImprirOrdem}
                                disabled={isGerando || selecionados.size === 0}
                                className="btn_ordem"
                            >
                                {isGerando ? "Gerando..." : "Gerar Tela"}
                            </button>
                        </div>

                        {/* Campo de pesquisa */}
                        <div className="search_ordem">
                            <input
                                type="text"
                                placeholder="Buscar por ordem, tela ou serial..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="search-input_ordem"
                            />
                            <span className="selecionados_ordem">
                                <strong>{selecionados.size}</strong> selecionada(s)
                            </span>
                        </div>

                        <div className="table_wrapper_ordem">
                            <table className="table_ordem">
                                <thead className="thead_ordem">
                                    <tr>
                                        <th>Tela</th>
                                        <th>Ordem</th>
                                        <th>Serial</th>
                                        <th>Data</th>
                                        <th className="acao_ordem">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhasVisiveis.length > 0 ? (
                                        linhasVisiveis.map((linha) => (
                                            <OrdemRow
                                                key={`${linha.ordem}-${linha.tela}`}
                                                linha={linha}
                                                selecionado={selecionados.has(linha.id)}
                                                onToggle={handleCheckboxChange}
                                            />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="vazio_ordem">
                                                Nenhuma ordem encontrada
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {linhasFiltradas.length > PAGE_SIZE && (
                            <div className="pagination_ordem">
                                <button
                                    type="button"
                                    onClick={() => setPage(currentPage - 1)}
                                    disabled={currentPage === 0}
                                >
                                    Anterior
                                </button>
                                <span className="pagination_ordem-info">
                                    Página {currentPage + 1} de {totalPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPage(currentPage + 1)}
                                    disabled={currentPage >= totalPages - 1}
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

interface OrdemRowProps {
    linha: LinhaOrdem;
    selecionado: boolean;
    onToggle: (id: number) => void;
}

/** Memoizado: ao marcar uma ordem, só a linha afetada é re-renderizada. */
const OrdemRow = React.memo(({ linha, selecionado, onToggle }: OrdemRowProps) => (
    <tr
        className={`row_ordem${selecionado ? " row_ordem--selecionada" : ""}`}
        onClick={() => onToggle(linha.id)}
    >
        <td>{linha.tela}</td>
        <td className="num_ordem">{linha.ordem}</td>
        <td className="num_ordem">{linha.lote}</td>
        <td>{linha.data}</td>
        <td className="acao_ordem">
            <input
                type="checkbox"
                checked={selecionado}
                onChange={() => onToggle(linha.id)}
                onClick={(e) => e.stopPropagation()}
                className="checkbox_ordem"
            />
        </td>
    </tr>
));
